import React, { useState } from 'react';
import { Calendar, CheckCircle, Clock, AlertTriangle, ChevronRight, TrendingUp, Plus } from 'lucide-react';

const CampaignOverview = ({ onViewCampaign = () => {} }) => {
  const campaignData = {
    active: 2,
    scheduled: 3,
    completed: 8,
    totalImpact: '+4.2%',
    marginImpact: '+0.08 ฿/L'
  };

  const recentCampaigns = [
    { id: 1, name: 'Summer Fuel Promotion', status: 'active', startDate: '2023-06-01', endDate: '2023-08-31', impact: '+5.3%' },
    { id: 2, name: 'Café Amazon Bundle', status: 'active', startDate: '2023-07-15', endDate: '2023-09-15', impact: '+3.8%' },
    { id: 3, name: 'Weekend Discount', status: 'scheduled', startDate: '2023-09-01', endDate: '2023-10-31', impact: 'N/A' },
    { id: 4, name: 'Loyalty Drive', status: 'scheduled', startDate: '2023-09-15', endDate: '2023-11-15', impact: 'N/A' },
    { id: 5, name: 'EV Charging Promo', status: 'scheduled', startDate: '2023-10-01', endDate: '2023-12-31', impact: 'N/A' },
  ];

  const [calendarView, setCalendarView] = useState('month');

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">Campaign Management</h1>
        <p className="text-gray-600">Monitor, analyze and create marketing campaigns across all segments.</p>
      </div>
      
      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-green-50 border border-green-100 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-600 mb-1">Active Campaigns</p>
              <h3 className="text-3xl font-semibold text-gray-900">{campaignData.active}</h3>
            </div>
            <div className="p-2 bg-green-100 rounded-md">
              <CheckCircle size={20} className="text-green-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-600 mb-1">Scheduled Campaigns</p>
              <h3 className="text-3xl font-semibold text-gray-900">{campaignData.scheduled}</h3>
            </div>
            <div className="p-2 bg-blue-100 rounded-md">
              <Clock size={20} className="text-blue-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-purple-50 border border-purple-100 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-600 mb-1">Volume Impact</p>
              <h3 className="text-3xl font-semibold text-gray-900">{campaignData.totalImpact}</h3>
            </div>
            <div className="p-2 bg-purple-100 rounded-md">
              <TrendingUp size={20} className="text-purple-600" />
            </div>
          </div>
        </div>
        
        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm text-gray-600 mb-1">Margin Impact</p>
              <h3 className="text-3xl font-semibold text-gray-900">{campaignData.marginImpact}</h3>
            </div>
            <div className="p-2 bg-indigo-100 rounded-md">
              <TrendingUp size={20} className="text-indigo-600" />
            </div>
          </div>
        </div>
      </div>
      
      {/* Calendar View */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Campaign Calendar</h2>
            <div className="flex space-x-2">
              <button 
                onClick={() => setCalendarView('month')}
                className={`px-3 py-1 rounded-md text-sm ${calendarView === 'month' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
              >
                Month
              </button>
              <button 
                onClick={() => setCalendarView('week')}
                className={`px-3 py-1 rounded-md text-sm ${calendarView === 'week' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
              >
                Week
              </button>
              <button 
                onClick={() => setCalendarView('day')}
                className={`px-3 py-1 rounded-md text-sm ${calendarView === 'day' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
              >
                Day
              </button>
            </div>
          </div>
        </div>
        <div className="p-4">
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-md border border-dashed border-gray-300">
            <div className="text-center">
              <Calendar size={40} className="mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">Calendar View Placeholder</p>
              <p className="text-sm text-gray-400">Campaign schedule visualization goes here</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent Campaigns */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium text-gray-900">Recent Campaigns</h2>
            <button className="text-sm text-purple-700 hover:text-purple-900 font-medium">View All</button>
          </div>
        </div>
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500">
                  <th className="px-4 py-2">Campaign Name</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Duration</th>
                  <th className="px-4 py-2">Impact</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {recentCampaigns.map((campaign) => (
                  <tr key={campaign.id} className="border-b border-gray-100 text-sm">
                    <td className="px-4 py-3 font-medium text-gray-900">{campaign.name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        campaign.status === 'active' ? 'bg-green-100 text-green-800' : 
                        campaign.status === 'scheduled' ? 'bg-blue-100 text-blue-800' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">
                      {new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900">{campaign.impact}</td>
                    <td className="px-4 py-3 text-right">
                      <button 
                        className="text-purple-600 hover:text-purple-900"
                        onClick={() => onViewCampaign(campaign.id)}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      
      {/* Create Campaign Button */}
      <div className="mt-6 flex justify-end">
        <button className="px-4 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800 flex items-center">
          <span className="mr-2">Create New Campaign</span>
          <Plus size={16} />
        </button>
      </div>
    </div>
  );
};

export default CampaignOverview;
